import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { baseUrl } from '../../utils/axios'
import { toast } from 'react-toastify'
import {
  IAddInventoryAssignmentState,
  IInventoryItem,
  IInventoryItemssSlice,
} from 'types/inventory.interface'
import { handleErrors, formatParams, getParams, dataURItoBlob } from 'utils/helpers'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IGetAllInventoryItemsSingleResponse,
  IGetAllInventoryAssignmentsSingleResponse,
  IInvenotryItemsGetSingleResponse,
  IInventoryItemPostRequest,
  IInventoryItemsDashboardSingleResponse,
  IInventoryAssignmentPostRequest,
} from 'types/api/inventoryItems.interface'
import axios, { AxiosResponse } from 'axios'
import { getFoodInventoryItems } from 'features/food/foodSlice'
import {
  barCodeTypes,
  costingMethodOptions,
  depreciationMethodOptions,
  expiryPolicyOptions,
  lifeCycleStartSources,
  lifeCycleTypes,
  trackingModeOptions,
} from 'pages/Inventory/Items/itemsData'

const initialState: IInventoryItemssSlice = {
  isLoading: false,
  inventoryItems: [],
  singleInventoryItem: null,
  totalCount: 0,
  totalPages: 0,
  assignments: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  dashboard: {
    data: [],
    totalCount: 0,
    totalPages: 0,
    lowStockCount: 0,
  },
}

export const getInventoryItems = createAsyncThunk(
  'inventoryItems/getInventoryItems',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IInventoryItemsDashboardSingleResponse>> =
        await baseUrl.get('inventory/item', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryItemsDashboard = createAsyncThunk(
  'inventoryItems/getInventoryItemsDashboard',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllInventoryItemsSingleResponse>> =
        await baseUrl.get('inventory/dashboard/item', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryAssignments = createAsyncThunk(
  'inventoryItems/getInventoryAssignments',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllInventoryAssignmentsSingleResponse>> =
        await baseUrl.get('inventory/assignment/request', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryItemsDashboardLowStock = createAsyncThunk(
  'inventoryItems/getInventoryItemsDashboardLowStock',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<{ lowStockCount: number }> = await baseUrl.get(
        'inventory/dashboard/low-stock-count',
        { params },
      )
      return resp.data.lowStockCount
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addInventoryAssignment = createAsyncThunk(
  'inventoryItems/addInventoryAssignment',
  async (data: IAddInventoryAssignmentState, thunkAPI) => {
    try {
      const obj: IInventoryAssignmentPostRequest = {
        type: data.type?.value,
        locationId: data.location?.value,
        assigneeUserId: data.assigneeUser?.value,
        lines: data.lines.map((line) => ({
          itemId: line.item?.value,
          serialIds: line.serials.every((s) => s.value)
            ? line.serials.filter((s) => s.value).map((s) => s.value)
            : null,
          quantity: line.quantity,
        })),
        notes: data.notes || '',
      }
      const resp: AxiosResponse = await baseUrl.post('inventory/assignment/request', obj)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addInventoryItem = createAsyncThunk(
  'inventoryItems/addInventoryItem',
  async (item: IInventoryItem, thunkAPI) => {
    try {
      const forSubmit: IInventoryItemPostRequest = {
        expiryPolicy: item?.expiryPolicy?.value,
        reorderPoint: Number(item.reorderPoint),
        barcodes: item.barcodes.map((b) => ({
          ...b,
          unitId: item.unit?.value,
          type: b.type?.value,
        })),
        depreciable: item.depreciable,
        glAssetCode: item.glAssetCode,
        glExpenseCode: item.glExpenseCode,
        depreciationMethod: item.depreciationMethod?.value,
        depreciationPeriodMonths: Number(item.depreciationPeriodMonths),
        description: item.description,
        leadTimeDays: Number(item.leadTimeDays),
        taxDeductible: item.taxDeductible,
        trackingMode: item.trackingMode?.value,
        warrantyMonths: Number(item.warrantyMonths),
        standardCost: Number(item.standardCost),
        hazardClass: item.hazardClass,
        costCenterId: item.costCenter?.value || null,
        costingMethod: item.costingMethod?.value,
        lifecycleExpirationType: item.lifecycleExpirationType?.value,
        lifecycleStartSourceType: item.lifecycleStartSourceType?.value,
        preferredSupplierId: item.preferredSupplier?.value,
        unitId: item.unit?.value,
        image: !!item.image,
        sku: item.sku,
        name: item.name,
        machineModels: item.machineModels.map((item) => {
          return {
            id: item.id,
            rating: Number(item.rating),
            priority: Number(item.priority),
          }
        }),
        categoryId: item.category?.value,
        maxLevel: Number(item.maxLevel),
        minLevel: Number(item.minLevel),
        nature: item.nature?.value != null ? Number(item.nature.value) : undefined,
        critical: item.critical,
        purchaseUnitId: item.purchaseUnit?.value != null ? Number(item.purchaseUnit.value) : undefined,
        stockUnitId: item.stockUnit?.value != null ? Number(item.stockUnit.value) : undefined,
        issueUnitId: item.issueUnit?.value != null ? Number(item.issueUnit.value) : undefined,
        conversionFactor:
          item.conversionFactor != null ? Number(item.conversionFactor) : undefined,
        fieldValues: item.fields
          ?.filter((f) => f.value)
          .map((item) => ({
            id: item.field.id,
            value: item.value,
          })),
      }

      const resp: AxiosResponse = await baseUrl.post('inventory/item', forSubmit, thunkAPI)

      if (item.image) {
        try {
          const blobedImage = dataURItoBlob(item.image)
          const url = resp.data.putImagePresignedUrl
          await axios.put(url, blobedImage)
          return resp.data
        } catch (error) {
          console.log(error)
        }
      }
      await thunkAPI.dispatch(getFoodInventoryItems(getParams({ location, limit: 5 })))
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleInventoryItem = createAsyncThunk(
  'inventoryItems/getSingleInventoryItem',
  async (id: string, thunkAPI) => {
    try {
      const { data }: AxiosResponse<IInvenotryItemsGetSingleResponse> = await baseUrl.get(
        'inventory/item/' + id,
        thunkAPI,
      )
      const obj: IInventoryItem = {
        unit: data.unit ? { label: data.unit.name, value: data.unit.id } : null,
        name: data.name,
        sku: data.sku,
        machineModels: data.machineModels.map((m) => ({
          id: m.machineModel.id,
          rating: m.rating,
          priority: m.priority,
          name: m.machineModel.model + ' ' + m.machineModel.make,
        })),
        costCenter: data.costCenter
          ? { label: data.costCenter.name, value: data.costCenter.id }
          : null,
        costingMethod: costingMethodOptions.find((c) => c.value == data.costingMethod),
        category: data.category ? { label: data.category.name, value: data.category.id } : null,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        fields: data.fieldValues.map((f) => ({ ...f, value: f.value?.id ?? f.value })),
        glAssetCode: data.glAssetCode,
        glExpenseCode: data.glExpenseCode,
        standardCost: data.standardCost,
        msdsAttachmentFileUrl: data.msdsAttachmentFileUrl,
        leadTimeDays: data.leadTimeDays,
        depreciable: data.depreciable,
        depreciationMethod: depreciationMethodOptions.find(
          (m) => m.value == data.depreciationMethod,
        ),
        depreciationPeriodMonths: data.depreciationPeriodMonths,
        description: data.description,
        taxDeductible: data.taxDeductible,
        lifecycleExpirationType: lifeCycleTypes.find(
          (l) => l.value == data.lifecycleExpirationType,
        ),
        lifecycleNumericExpirationValue: data.lifecycleExpirationType,
        lifecycleStartSourceType: lifeCycleStartSources.find(
          (s) => s.value == data.lifecycleStartSourceType,
        ),
        maxLevel: data.maxLevel,
        minLevel: data.minLevel,
        barcodes: data.barcodes.map((b) => ({
          id: b.id,
          code: b.code,
          type: barCodeTypes.find((t) => t.value === b.type) || null,
          quantity: b.quantity,
          unit: { label: b.unit.name, value: b.unit.id },
        })),
        trackingMode: trackingModeOptions.find((t) => t.value == data.trackingMode),
        expiryPolicy: expiryPolicyOptions.find((p) => p.value == data.expiryPolicy),
        warrantyMonths: data.warrantyMonths,
        reorderPoint: data.reorderPoint,
        id: data.id,
        preferredSupplier: data.preferredSupplier
          ? { label: data.preferredSupplier.name, value: data.preferredSupplier.id }
          : null,
        hazardClass: data.hazardClass,
        nature: data.nature != null ? { value: data.nature, label: data.nature === 2 ? 'RETURNABLE' : 'CONSUMABLE' } : null,
        critical: Boolean(data.critical),
        purchaseUnit: data.purchaseUnit
          ? { value: data.purchaseUnit.id, label: data.purchaseUnit.name }
          : null,
        stockUnit: data.stockUnit
          ? { value: data.stockUnit.id, label: data.stockUnit.name }
          : null,
        issueUnit: data.issueUnit
          ? { value: data.issueUnit.id, label: data.issueUnit.name }
          : null,
        conversionFactor: data.conversionFactor,
      }
      return obj
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const attachMSDS = createAsyncThunk(
  'inventoryItems/attachMSDS',
  async ({ file, id }: { file: File; id: number }, thunkAPI) => {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const resp: AxiosResponse = await baseUrl.post(
        'inventory/item/' + id + '/msds-attachment',
        formData,
      )
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteMSDS = createAsyncThunk(
  'inventoryItems/deleteMSDS',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.delete('inventory/item/' + id + '/msds-attachment')
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateInventoryItem = createAsyncThunk(
  'inventoryItems/updateInventoryItem',
  async (item: IInventoryItem, thunkAPI) => {
    try {
      const forSubmit: IInventoryItemPostRequest = {
        expiryPolicy: item.expiryPolicy?.value,
        reorderPoint: Number(item.reorderPoint),
        barcodes: item.barcodes.map((b) => ({
          ...b,
          unitId: item.unit?.value,
          type: b.type?.value,
        })),
        depreciable: item.depreciable,
        glAssetCode: item.glAssetCode,
        glExpenseCode: item.glExpenseCode,
        depreciationMethod: item.depreciationMethod?.value,
        depreciationPeriodMonths: Number(item.depreciationPeriodMonths),
        description: item.description,
        leadTimeDays: Number(item.leadTimeDays),
        taxDeductible: item.taxDeductible,
        trackingMode: item.trackingMode?.value,
        warrantyMonths: Number(item.warrantyMonths),
        standardCost: Number(item.standardCost),
        hazardClass: item.hazardClass,
        costCenterId: item.costCenter?.value,
        costingMethod: item.costingMethod?.value,
        lifecycleExpirationType: item.lifecycleExpirationType?.value,
        lifecycleStartSourceType: item.lifecycleStartSourceType?.value,
        preferredSupplierId: item.preferredSupplier?.value,
        unitId: item.unit?.value,
        image: !!item.image,
        sku: item.sku,
        name: item.name,
        machineModels: item.machineModels.map((item) => {
          return {
            id: item.id,
            rating: Number(item.rating),
            priority: Number(item.priority),
          }
        }),
        categoryId: item.category?.value,
        maxLevel: Number(item.maxLevel),
        minLevel: Number(item.minLevel),
        nature: item.nature?.value != null ? Number(item.nature.value) : undefined,
        critical: item.critical,
        purchaseUnitId: item.purchaseUnit?.value != null ? Number(item.purchaseUnit.value) : undefined,
        stockUnitId: item.stockUnit?.value != null ? Number(item.stockUnit.value) : undefined,
        issueUnitId: item.issueUnit?.value != null ? Number(item.issueUnit.value) : undefined,
        conversionFactor:
          item.conversionFactor != null ? Number(item.conversionFactor) : undefined,
        fieldValues: item.fields
          ?.filter((f) => f.value)
          .map((item) => ({
            id: item.field.id,
            value: item.value,
          })),
      }

      const resp = await baseUrl.patch('inventory/item/' + item.id, forSubmit, thunkAPI)

      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteInventoryItem = createAsyncThunk(
  'inventoryItems/deleteInventoryItem',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('inventory/item/' + id, thunkAPI)
      await thunkAPI.dispatch(getFoodInventoryItems(getParams({ location, limit: 5 })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const approveInventoryAssignment = createAsyncThunk(
  'inventoryItems/approveInventoryAssignment',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post(`inventory/assignment/request/${id}/approve`, thunkAPI)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const declineInventoryAssignment = createAsyncThunk(
  'inventoryItems/declineInventoryAssignment',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post(`inventory/assignment/request/${id}/decline`, thunkAPI)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const notifyUserForInventoryAssignment = createAsyncThunk(
  'inventoryItems/notifyUserForInventoryAssignment',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post(`inventory/assignment/request/${id}/notify`, thunkAPI)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const inventoryItemsSlice = createSlice({
  name: 'inventoryItems',
  initialState,
  reducers: {
    clearInventoryItem: (state) => {
      state.singleInventoryItem = null
    },
    clearInventoryAssignments: (state) => {
      state.assignments.data = []
      state.assignments.totalCount = 0
      state.assignments.totalPages = 0
    },
    clearInventoryItemsList: (state) => {
      state.inventoryItems = []
      state.totalCount = 0
      state.totalPages = 0
    },
    clearInventoryDashboard: (state) => {
      state.dashboard = {
        data: [],
        totalCount: 0,
        totalPages: 0,
        lowStockCount: 0,
      }
    },
  },
  extraReducers: {
    [addInventoryAssignment.pending.type]: (state) => {
      state.isLoading = true
    },
    [addInventoryAssignment.fulfilled.type]: (state: IInventoryItemssSlice) => {
      state.isLoading = false
      toast.success('Inventory assignment added')
    },
    [addInventoryAssignment.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [notifyUserForInventoryAssignment.pending.type]: (state) => {
      state.isLoading = true
    },
    [notifyUserForInventoryAssignment.fulfilled.type]: (state: IInventoryItemssSlice) => {
      state.isLoading = false
      toast.success('Employee notified for inventory request')
    },
    [notifyUserForInventoryAssignment.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [approveInventoryAssignment.pending.type]: (state) => {
      state.isLoading = true
    },
    [approveInventoryAssignment.fulfilled.type]: (state: IInventoryItemssSlice) => {
      state.isLoading = false
      toast.success('Inventory assignment approved')
    },
    [approveInventoryAssignment.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [declineInventoryAssignment.pending.type]: (state) => {
      state.isLoading = true
    },
    [declineInventoryAssignment.fulfilled.type]: (state: IInventoryItemssSlice) => {
      state.isLoading = false
      toast.success('Inventory assignment declined')
    },
    [declineInventoryAssignment.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addInventoryItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [addInventoryItem.fulfilled.type]: (state: IInventoryItemssSlice) => {
      state.isLoading = false
      toast.success('Inventory item added')
    },
    [addInventoryItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      // check if msg path is good
      handleErrors(payload)
    },
    [getInventoryItemsDashboard.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryItemsDashboard.fulfilled.type]: (state: IInventoryItemssSlice, { payload }) => {
      state.isLoading = false
      state.dashboard = payload
    },
    [getInventoryItemsDashboard.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getInventoryItemsDashboardLowStock.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryItemsDashboardLowStock.fulfilled.type]: (
      state: IInventoryItemssSlice,
      { payload },
    ) => {
      state.isLoading = false
      state.dashboard.lowStockCount = payload
    },
    [getInventoryItemsDashboardLowStock.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getInventoryAssignments.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryAssignments.fulfilled.type]: (
      state: IInventoryItemssSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllInventoryAssignmentsSingleResponse> },
    ) => {
      state.isLoading = false
      state.assignments = payload
    },
    [getInventoryAssignments.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getInventoryItems.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryItems.fulfilled.type]: (
      state: IInventoryItemssSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllInventoryItemsSingleResponse> },
    ) => {
      state.isLoading = false
      state.inventoryItems = payload.data
      state.totalPages = payload.totalPages
      state.totalCount = payload.totalCount
    },
    [getInventoryItems.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleInventoryItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleInventoryItem.fulfilled.type]: (state, { payload }) => {
      // TODO tip
      state.isLoading = false
      state.singleInventoryItem = {
        ...payload,
        image: payload.getImagePresignedUrl,
      }
    },
    [getSingleInventoryItem.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
      state.isLoading = false
    },
    [updateInventoryItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [updateInventoryItem.fulfilled.type]: (state: IInventoryItemssSlice, { payload }) => {
      const foundIndex = state.inventoryItems.findIndex((item) => item.id === payload.id)
      state.inventoryItems[foundIndex] = payload
      state.isLoading = false
      toast.success('Item updated')
    },
    [updateInventoryItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteInventoryItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteInventoryItem.fulfilled.type]: (state: IInventoryItemssSlice) => {
      state.isLoading = false
      toast.success('Item deleted')
    },
    [deleteInventoryItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [attachMSDS.pending.type]: (state) => {
      state.isLoading = true
    },
    [attachMSDS.fulfilled.type]: (state) => {
      state.isLoading = false
    },
    [attachMSDS.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const {
  clearInventoryItem,
  clearInventoryItemsList,
  clearInventoryDashboard,
  clearInventoryAssignments,
} = inventoryItemsSlice.actions

export default inventoryItemsSlice.reducer
